import { useState, useEffect, useRef } from 'react';
import { useDeviceDetection } from './use-mobile';

export interface ResponsiveLayout {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  orientation: 'portrait' | 'landscape';
  screenSize: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  columns: number;
  spacing: 'compact' | 'normal' | 'spacious';
}

/**
 * Hook que proporciona información detallada sobre el layout
 * SIN cambiar layout por orientación - evita reflows en iOS con rotation lock
 */
export const useResponsiveLayout = (): ResponsiveLayout => {
  const { isMobile, isIOS, isAndroid } = useDeviceDetection();
  
  // Guardar valores iniciales para evitar reflows
  const initialValuesRef = useRef<{
    columns: number;
    orientation: 'portrait' | 'landscape';
  } | null>(null);
  
  const [layout, setLayout] = useState<ResponsiveLayout>({
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isIOS: false,
    isAndroid: false,
    orientation: 'portrait',
    screenSize: 'md',
    columns: 3,
    spacing: 'normal',
  });

  useEffect(() => {
    const updateLayout = () => {
      const width = window.innerWidth;

      // Determinar tamaño de pantalla SOLO por width
      let screenSize: ResponsiveLayout['screenSize'] = 'md';
      if (width < 640) screenSize = 'xs';
      else if (width < 768) screenSize = 'sm';
      else if (width < 1024) screenSize = 'md';
      else if (width < 1280) screenSize = 'lg';
      else if (width < 1536) screenSize = 'xl';
      else screenSize = '2xl';

      // Determinar tipo de dispositivo SOLO por width
      const isMobileDevice = width < 768;
      const isTabletDevice = width >= 768 && width < 1024;
      const isDesktopDevice = width >= 1024;

      // Columnas fijas por tipo de dispositivo - NO cambia por orientación
      let columns: number;
      if (isMobileDevice) {
        columns = 1;
      } else if (isTabletDevice) {
        columns = 2;
      } else {
        columns = 4;
      }

      // Guardar valores iniciales para no cambiar después
      if (!initialValuesRef.current) {
        initialValuesRef.current = {
          columns,
          orientation: 'portrait', // Siempre portrait para consistencia
        };
      }

      // Determinar espaciado
      let spacing: ResponsiveLayout['spacing'] = 'normal';
      if (isMobileDevice) spacing = 'compact';
      else if (isDesktopDevice) spacing = 'spacious';

      setLayout({
        isMobile: isMobileDevice,
        isTablet: isTabletDevice,
        isDesktop: isDesktopDevice,
        isIOS,
        isAndroid,
        orientation: 'portrait', // Siempre portrait - evita cambios de layout
        screenSize,
        columns,
        spacing,
      });
    };

    updateLayout();
    
    // SOLO escuchar resize, NO orientationchange para evitar reflows
    window.addEventListener('resize', updateLayout);

    return () => {
      window.removeEventListener('resize', updateLayout);
    };
  }, [isIOS, isAndroid]);

  return layout;
};

/**
 * Hook para obtener clases CSS específicas del dispositivo
 */
export const useDeviceClasses = () => {
  const layout = useResponsiveLayout();

  const getSpacingClass = () => {
    switch (layout.spacing) {
      case 'compact':
        return 'space-y-3 p-3';
      case 'spacious':
        return 'space-y-8 p-8';
      default:
        return 'space-y-6 p-6';
    }
  };

  // Grid fijo por tipo de dispositivo - NO depende de orientación
  const getGridClass = () => {
    if (layout.isMobile) {
      return 'grid-cols-1';
    }
    if (layout.isTablet) {
      return 'grid-cols-2';
    }
    return 'grid-cols-4';
  };

  const getContainerClass = () => {
    if (layout.isMobile) return 'max-w-full px-4';
    if (layout.isTablet) return 'max-w-4xl px-6 mx-auto';
    return 'max-w-7xl px-8 mx-auto';
  };

  const getCardClass = () => {
    if (layout.isMobile) return 'p-4 text-sm';
    if (layout.isTablet) return 'p-5 text-base';
    return 'p-6 text-base';
  };

  const getButtonClass = () => {
    if (layout.isMobile) return 'text-sm px-4 py-2.5';
    return 'text-base px-6 py-3';
  };

  const getTextSizeClass = () => {
    if (layout.isMobile) {
      return {
        heading: 'text-xl',
        subheading: 'text-base',
        body: 'text-sm',
        caption: 'text-xs',
      };
    }
    return {
      heading: 'text-3xl',
      subheading: 'text-xl',
      body: 'text-base',
      caption: 'text-sm',
    };
  };

  return {
    spacing: getSpacingClass(),
    grid: getGridClass(),
    container: getContainerClass(),
    card: getCardClass(),
    button: getButtonClass(),
    textSize: getTextSizeClass(),
    layout,
  };
};
